import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import './App.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import ErrorBoundary from './components/ErrorBoundary';

import Contacts from './pages/Contacts';
import LeadSpecialist from './pages/LeadSpecialist';
import CandidateDashboard from './pages/CandidateDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import CoursesPage from './pages/CoursesPage';
import AdminCoursesPage from './pages/AdminCoursesPage';
import Forms from './pages/Forms';
import PublicForm from './pages/PublicForm';
import PublicJobView from './pages/PublicJobView';
import PageContent from './pages/PageContent';
import PublicHome from './pages/PublicHome';
import EmployerHome from './pages/EmployerHome';
import TelesalesCRM from './pages/TelesalesCRM';
import ReschedulePage from './pages/ReschedulePage';
import SurveyPage from './pages/SurveyPage';
import ReferAndEarn from './pages/ReferAndEarn';
import ViewQuestion from './pages/ViewQuestion';
import ViewQuestions from './pages/ViewQuestions';
import AuthCallback from './pages/AuthCallback';
import OAuthCallback from './pages/OAuthCallback';
import PublicSupport from './pages/PublicSupport';
import OfficiumPagesWorkspace from './pages/OfficiumPagesWorkspace';
import SheetsWorkspace from './pages/SheetsWorkspace';
import DataBackupPage from './pages/DataBackupPage';
import BackupPreviewPage from './pages/BackupPreviewPage';
import TeamManagementPage from './pages/TeamManagementPage';
import SupportFeedbackPage from './pages/SupportFeedbackPage';
import SplashScreen from './components/SplashScreen';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getAuthToken = () => localStorage.getItem('token');
export const setAuthToken = (token) => localStorage.setItem('token', token);
export const removeAuthToken = () => localStorage.removeItem('token');

// Update app badge for notifications
export const updateAppBadge = (count) => {
  // Update PWA badge (if supported)
  if ('setAppBadge' in navigator) {
    if (count > 0) {
      navigator.setAppBadge(count).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  }
  
  // Update document title with notification count
  if (count > 0) {
    document.title = `(${count}) Officium Connect`;
  } else {
    document.title = 'Officium Connect';
  }
};

export const isAuthenticated = () => {
  const token = getAuthToken();
  if (!token) return false;
  
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const PrivateRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

// Route that blocks telesales users from accessing non-telesales pages
const NonTelesalesRoute = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUserRole(data.role);
        }
      } catch (error) {
        console.error('Failed to fetch user role:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserRole();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
    </div>;
  }

  // Telesales users can only access /telesales
  if (userRole === 'telesales') {
    return <Navigate to="/telesales" replace />;
  }

  return children;
};

// Strict Admin Route - Only allows users with is_admin=true
const AdminRoute = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.is_admin === true);
          setUserRole(data.role);
        }
      } catch (error) {
        console.error('Failed to check admin access:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAdminAccess();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
    </div>;
  }

  // Redirect non-admins to appropriate dashboard
  if (!isAdmin) {
    if (userRole === 'candidate') {
      return <Navigate to="/candidate" replace />;
    } else if (userRole === 'lead_specialist') {
      return <Navigate to="/lead-specialist" replace />;
    } else if (userRole === 'telesales') {
      return <Navigate to="/telesales" replace />;
    } else if (userRole === 'employer') {
      return <Navigate to="/employer-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Smart route component that redirects based on user role
const RoleBasedRoute = () => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUserRole(data.role);
        }
      } catch (error) {
        console.error('Failed to fetch user role:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserRole();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
    </div>;
  }

  // Redirect based on role
  if (userRole === 'candidate') {
    return <Navigate to="/candidate" replace />;
  } else if (userRole === 'lead_specialist') {
    return <Navigate to="/lead-specialist" replace />;
  } else if (userRole === 'telesales') {
    return <Navigate to="/telesales" replace />;
  } else {
    return <Dashboard />;
  }
};

// Component to check for OAuth callback in URL hash
const OAuthCallbackChecker = ({ children }) => {
  const location = useLocation();
  
  // Check URL hash for session_id (OAuth callback) synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return children;
};

function App() {
  // Show splash screen on initial load
  const [showSplash, setShowSplash] = useState(true);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  return (
    <div className="App">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <BrowserRouter>
        <OAuthCallbackChecker>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
          {/* OAuth Callbacks for Integrations */}
          <Route path="/auth/google/callback" element={<OAuthCallback />} />
          <Route path="/auth/slack/callback" element={<OAuthCallback />} />
          <Route path="/auth/discord/callback" element={<OAuthCallback />} />
            
            {/* Public Landing Pages */}
            <Route path="/" element={<PublicHome />} />
            <Route path="/jobs" element={<PublicHome />} />
            <Route path="/employer" element={<EmployerHome />} />
            
            {/* Survey Page (Public) */}
            <Route path="/survey/:token" element={<SurveyPage />} />
          
          {/* Protected Routes */}
          <Route path="/officium-pages" element={<PrivateRoute><OfficiumPagesWorkspace /></PrivateRoute>} />
          <Route path="/sheets" element={<PrivateRoute><SheetsWorkspace /></PrivateRoute>} />
          <Route path="/data-backup" element={<PrivateRoute><DataBackupPage /></PrivateRoute>} />
          <Route path="/team-management" element={<PrivateRoute><TeamManagementPage /></PrivateRoute>} />
          <Route path="/support-feedback/:token" element={<SupportFeedbackPage />} />
          <Route path="/backup-preview/:snapshotId" element={<PrivateRoute><BackupPreviewPage /></PrivateRoute>} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <NonTelesalesRoute>
                  <Dashboard />
                </NonTelesalesRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/lead-specialist"
            element={
              <PrivateRoute>
                <NonTelesalesRoute>
                  <LeadSpecialist />
                </NonTelesalesRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/contacts"
            element={
              <PrivateRoute>
                <NonTelesalesRoute>
                  <Contacts />
                </NonTelesalesRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/forms"
            element={
              <PrivateRoute>
                <NonTelesalesRoute>
                  <Forms />
                </NonTelesalesRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/candidate"
            element={
              <PrivateRoute>
                <NonTelesalesRoute>
                  <CandidateDashboard />
                </NonTelesalesRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/employer-dashboard"
            element={
              <PrivateRoute>
                <NonTelesalesRoute>
                  <EmployerDashboard />
                </NonTelesalesRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/telesales"
            element={
              <PrivateRoute>
                <TelesalesCRM />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <PrivateRoute>
                <NonTelesalesRoute>
                  <CoursesPage />
                </NonTelesalesRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseId"
            element={
              <PrivateRoute>
                <CoursesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/courses"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AdminCoursesPage />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route path="/form/:formId" element={<PublicForm />} />
          <Route path="/job/:jobId" element={<PublicJobView />} />
          <Route path="/support" element={<PublicSupport />} />
          <Route path="/reschedule/:token" element={<ReschedulePage />} />
          <Route path="/refer" element={<ReferAndEarn />} />
          <Route path="/refer-and-earn" element={<ReferAndEarn />} />
          <Route path="/view-question/:questionId" element={<ViewQuestion />} />
          <Route path="/view-questions/:jobTitle" element={<ViewQuestions />} />
          {/* Keep /home as alias */}
          <Route path="/home" element={<PublicHome />} />
          {/* Public Pages - Footer Links */}
          <Route path="/our-story" element={<PageContent />} />
          <Route path="/reviews" element={<PageContent />} />
          <Route path="/pricing" element={<PageContent />} />
          <Route path="/contact" element={<PageContent />} />
          <Route path="/faq" element={<PageContent />} />
          <Route path="/terms" element={<PageContent />} />
          <Route path="/privacy" element={<PageContent />} />
          <Route path="/refund" element={<PageContent />} />
          <Route path="/verification" element={<PageContent />} />
          <Route path="/page/:slug" element={<PageContent />} />
          </Routes>
        </OAuthCallbackChecker>
      </BrowserRouter>
      <Toaster position="bottom-left" />
    </div>
  );
}

export default App;